import { FC, useRef, forwardRef, ChangeEvent, DragEvent } from 'react';
import { LinearProgress } from '@mui/material';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { FiUploadCloud } from 'react-icons/fi';
import { MdOutlineAddToPhotos } from 'react-icons/md';
import { FaRegFilePdf } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import RemoveButton from '../../../assets/image/icons/red_x.svg';
import { LoggerService } from '../../utils';
import { VideoEditorPropTypes } from './types/propsType';
import { VideoEditorSize } from './types/enums';
import styles from './VideoEditor.module.scss';

/**
 * Processes input file to a readable Blob format URL
 * @param file - uploaded file
 * @returns Blob URL of the file
 */
const processFileToBlob = (file: File) => URL.createObjectURL(file);

const VideoEditor: FC<VideoEditorPropTypes> = forwardRef<HTMLDivElement, VideoEditorPropTypes>(
  (props: VideoEditorPropTypes, ref) => {
    const {
      maxFileCount,
      onFileUpload,
      onDelete,
      handleDownload,
      videoEditorSize = styles[VideoEditorSize.INHERIT_SIZE],
      uploadCTALabel,
      dragCTALabel,
      addAnotherFile,
      connectorLabel,
      fileTypeLabel,
      multiple = false,
      files,
      tooltip,
      disabled,
      progress,
      // showThumbnails,
      ...restProps
    } = props;

    const { t } = useTranslation();

    const inputFileRef = useRef<HTMLInputElement>(null);
    const inputFileUploadClick = () => {
      if (inputFileRef.current) {
        inputFileRef.current.click();
      } else {
        LoggerService.log('File input reference is null');
      }
    };

    // Filters FileList according to provided props
    const filterFiles = (files: FileList | null) => {
      let listFile: Record<number, File> = {};
      if (files) {
        listFile = { 0: files[0] };
        const limit = maxFileCount ?? Object.keys(files).length;
        if (multiple) {
          for (let i = 1; i < limit; i++) {
            if (!files[i]) {
              break;
            }
            listFile[i] = files[i];
          }
        }
      }
      return listFile;
    };

    const handleFileUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      onFileUpload?.({
        ...e,
        files: filterFiles(files),
      });
      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }
    };

    const handleDrag = function (e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = function (e: DragEvent) {
      handleDrag(e);
      onFileUpload?.({
        ...e,
        files: filterFiles(e.dataTransfer.files),
      });
    };

    const sizeMappings: Record<string, typeof styles> = {
      [VideoEditorSize.FIXED_SIZE]: styles[VideoEditorSize.FIXED_SIZE],
      [VideoEditorSize.FIXED_HEIGHT]: styles[VideoEditorSize.FIXED_HEIGHT],
      [VideoEditorSize.INHERIT_SIZE]: styles[VideoEditorSize.INHERIT_SIZE],
    } as unknown as Record<string, typeof styles>;

    const sizeClassName = sizeMappings[videoEditorSize];

    return (
      <div className={`${styles['file-upload']}`}>
        {/* Render multiple progress indicators */}
        {!disabled &&
          progress?.map((fileObject, index) => (
            <>
              {fileObject?.percentage !== 100 && (
                <div key={index} className={`${styles['file-upload-container']}`}>
                  <div className={`${styles['linear-progress']}`}>
                    <article className={`${styles['file-desc-text']} ${styles['upload']}`}>
                      <div>
                        {fileObject?.file?.file?.name} {fileObject?.percentage === 100 ? `uploaded` : `uploading`}
                      </div>
                      <div>{fileObject?.percentage === 100 && <IoIosCheckmarkCircle />}</div>
                    </article>
                    <div className={`${styles['linear-progress-wrapper']}`}>
                      <LinearProgress
                        variant="determinate"
                        className={`${styles['linear-progress-wrapper-item']}`}
                        value={fileObject?.percentage ?? 0}
                      />
                      <span className={styles['file-desc-text-subtitle']}>{fileObject?.percentage?.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ))}

        {(!files || files.length <= 0) && !disabled && (!progress || progress?.length <= 0) && (
          <div className={`${styles['file-upload-container']} ${sizeClassName}`}>
            <section
              className={`${styles['file-upload-section']} ${sizeClassName}`}
              onClick={inputFileUploadClick}
              onKeyDown={(e) => e.key === 'Enter' && inputFileUploadClick()}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onFocus={() => null}
              data-testid="file-editor-container-testid"
              role="presentation"
              ref={ref}
            >
              <FiUploadCloud className={styles['file-desc-text']} />
              <article className={styles['file-desc-text']}>
                <span>
                  <span>{dragCTALabel || t('admin.participant.dragAndDrop')}</span>
                  <span>{` ${connectorLabel || t('admin.participant.or')} `}</span>
                  <span className={styles['file-cta-label']}>{` ${uploadCTALabel || t('admin.participant.selectFileToUpload')} `}</span>
                </span>
              </article>
              <div className={styles['file-desc-text-subtitle']}>{fileTypeLabel}</div>
              <input
                type="file"
                ref={inputFileRef}
                className={styles['input-file-upload']}
                onChange={handleFileUploadChange}
                data-testid="input-file-upload-testid"
                multiple={multiple}
                {...restProps}
              />
            </section>
          </div>
        )}

        {/* Render all thumbnails in a single container */}
        {files && files.length > 0 && (
          <div className={`${styles['file-upload-container']} ${sizeClassName}`}>
            <div className={`${styles['thumbnail']}`}>
              {files.map((fileObject, index) => (
                <div
                  className={styles['thumbnail-item']}
                  onClick={() => {
                    if (fileObject?.photoId) handleDownload?.(fileObject?.photoId);
                  }}
                >
                  {!disabled && (
                    <div className={styles['thumbnail-item-delete']} onClick={() => onDelete?.(index)}>
                      <img src={RemoveButton} alt={'remove'} />
                    </div>
                  )}
                  {fileObject?.file?.type === 'application/pdf' || fileObject?.type === 'application/pdf' ? (
                    <div className={styles['thumbnail-item-image']}>
                      <FaRegFilePdf />
                    </div>
                  ) : (
                    <img
                      src={fileObject?.url}
                      alt={fileObject?.file?.name}
                      className={styles['thumbnail-item-image']}
                    />
                  )}
                </div>
              ))}

              {!props.disabled && (
                <div className={styles['thumbnail-item-add-wrapper']} onClick={inputFileUploadClick} ref={ref}>
                  <div className={`${styles['thumbnail-item']} ${styles['add-button']}`}>
                    <MdOutlineAddToPhotos />
                  </div>
                  <div>
                    <div className={styles['file-cta-label']}>{addAnotherFile || t('admin.participant.addAnotherFile')}</div>
                    <div className={styles['file-desc-text-subtitle']}>{fileTypeLabel}</div>
                  </div>
                  <input
                    type="file"
                    ref={inputFileRef}
                    className={styles['input-file-upload']}
                    onChange={handleFileUploadChange}
                    data-testid="input-file-upload-testid"
                    multiple={multiple}
                    {...restProps}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {tooltip && !props.disabled && <div className={`${styles['file-upload-tooltip']}`}>{tooltip}</div>}
      </div>
    );
  },
);

VideoEditor.displayName = 'VideoEditor';
export { processFileToBlob };
export default VideoEditor;
